// A "careful human" driver for sim (steering wheel) mode, for headless checks:
// pure-pursuit steering through the fixed rack with arm-like smoothing, a grip
// limit felt through the wheel, countersteer, throttle that eases off on
// wheelspin, a brake pedal modulated to avoid lock-ups, and paddle shifts.
//
// Human limits are explicit (HUMAN_LIMITS): the countersteer works on the car as
// it was a tracking delay ago, lock-ups and wheelspin only register after a
// reaction time, and the rim turns no faster than a hand can turn a G27. The
// driver also knows the car the way a practised driver does: braking points with
// a margin, the brake pedal that locks the fronts at each speed, and how much
// throttle the rears take while cornering (friction circle by feel).
import type { Car, CarInput } from '../../src/sim/car';
import { carParams } from '../../src/sim/car';
import { AIR_DENSITY, aeroParams } from '../../src/sim/aero';
import { ERS_MAX_KW, powerParams } from '../../src/sim/powerUnit';
import { Autopilot } from './autopilot';

const FULL_POWER_W = (powerParams.icePowerKW.value + ERS_MAX_KW) * 1000;
/** Tuning knobs (exported so experiments can vary them). */
export const DRIVER_TUNING = {
  /** Share of the rears' spare grip the driver dares to use on the throttle. */
  tractionMargin: 0.9,
  /** Braking points: share of the grip-limited deceleration a careful driver plans for. */
  brakePoints: 0.65,
  /** Share of the front lock-up pedal the driver brakes at, from experience. */
  brakeMargin: 0.92,
  /** Front wheel angle per rad of heading error to the lookahead point (at most). */
  pursuit: 1.1,
  /** ...and at most this multiple of geometric pure pursuit (which softens it at speed). */
  pursuitGeometric: 2.5,
  /** Throttle that holds a corner's speed, and extra per m/s below the target speed. */
  holdThrottle: 0.25,
  throttleGain: 0.3,
  /**
   * Most lock the driver winds on: the geometric angle for the tightest radius the grip
   * allows plus this share of the front tyres' peak slip angle. The aligning torque (FFB)
   * peaks at about half the peak slip and then goes light: that's where a driver stops.
   */
  feelSlip: 0.7,
  /** Countersteer: front wheel angle per rad of (tracked) body slip. */
  countersteer: 0.75,
};

function feelLimit(car: Car): number {
  const v = Math.max(car.speed, 1);
  const downforce = 0.5 * AIR_DENSITY * car.aero.clA * v * v;
  const aLat = carParams.tyreMu.value * (9.81 + downforce / carParams.mass.value);
  const geometric = (carParams.wheelbase.value * aLat) / (v * v);
  const peakSlip = Math.tan(Math.PI / (2 * carParams.tyreC.value)) / carParams.tyreB.value;
  return Math.min(carParams.steerLockDeg.value * (Math.PI / 180), geometric + DRIVER_TUNING.feelSlip * peakSlip);
}

/** How hard the car is cornering, felt as lateral g against what it can pull at this speed, 0..1. */
function latUse(car: Car): number {
  const downforce = 0.5 * AIR_DENSITY * car.aero.clA * car.speed * car.speed;
  const latMax = carParams.tyreMu.value * (9.81 + downforce / carParams.mass.value);
  return Math.min(0.99, Math.abs(car.latAccel) / latMax);
}

/**
 * The brake pedal at which the fronts would lock here, as a driver who knows the
 * car has learned it: front grip (weight, downforce, braking weight transfer at
 * the deceleration that grip allows) over the front's share of the brake force.
 */
function brakeThreshold(car: Car): number {
  if (!car.simMode) return 1;
  const v = Math.max(car.speed, 5);
  const m = carParams.mass.value;
  const downforce = 0.5 * AIR_DENSITY * car.aero.clA * v * v;
  const mu = carParams.tyreMu.value * Math.sqrt(1 - latUse(car) ** 2);
  const decel = (mu * (m * 9.81 + downforce)) / m;
  const front = m * 9.81 * carParams.frontWeight.value + downforce * aeroParams.balanceFront.value + (m * decel * carParams.cgHeight.value) / carParams.wheelbase.value;
  // Load sensitivity: the loaded fronts grip a little less per newton (same law as the car).
  const ref = m * 9.81 * carParams.frontWeight.value + 0.5 * AIR_DENSITY * 55 * 55 * aeroParams.clACorner.value * aeroParams.balanceFront.value;
  const sens = Math.max(0.3, 1 - carParams.loadSensitivity.value * (front / ref - 1));
  const bias = carParams.brakeBias.value + carParams.brakeByWireOffset.value + carParams.brakeMigration.value * (1 - Math.min(1, car.vx / 70));
  return Math.min(1, (mu * sens * front) / (bias * carParams.brakeMaxG.value * m * 9.81));
}

/**
 * The throttle a driver who knows the car would hold: the rears' grip left over
 * from cornering (friction circle, with the cornering load felt as lateral g),
 * as a share of the drive the engine could put down here, through the throttle map.
 */
function tractionThrottle(car: Car): number {
  const v = Math.max(car.speed, 5);
  const m = carParams.mass.value;
  const downforce = 0.5 * AIR_DENSITY * car.aero.clA * v * v;
  const lat = latUse(car);
  const rearLoad = m * 9.81 * (1 - carParams.frontWeight.value) + downforce * (1 - aeroParams.balanceFront.value);
  const rearGrip = carParams.tyreMu.value * (car.simMode ? carParams.rearGrip.value : 1) * rearLoad;
  const fullDrive = (FULL_POWER_W * carParams.drivetrainEff.value) / v;
  const torque = (DRIVER_TUNING.tractionMargin * rearGrip * Math.sqrt(1 - lat * lat)) / fullDrive;
  return Math.max(0.05, Math.min(1, Math.pow(Math.min(1, torque), 1 / carParams.throttleMap.value)));
}

export interface HumanLimits {
  /**
   * Effective delay of continuous steering corrections (tracking the car's yaw
   * and body slip). Skilled operators anticipate, so it's shorter than a
   * reaction: McRuer's crossover model puts it at ~0.1–0.15 s.
   */
  tracking: number;
  /** Perception + reaction time to discrete events (wheelspin, lock-ups), s. */
  reaction: number;
  /** Fastest rim speed, deg/s (a G27 rim turned hard by hand). */
  rimRate: number;
  /** Rim rotation lock to lock, deg (the game's wheel rotation setting). */
  rotation: number;
}

/** A decent sim racer on a G27: ~0.1 s tracking delay, ~0.2 s to react to a lock-up or wheelspin, ~700°/s at the rim. */
export const HUMAN_LIMITS: HumanLimits = { tracking: 0.1, reaction: 0.2, rimRate: 700, rotation: 360 };

interface Seen {
  t: number;
  beta: number;
  rearSpin: number;
  lock: number;
}

export class WheelDriver {
  private readonly pilot: Autopilot;
  private idx = 0;
  private steer = 0;
  private throttle = 0;
  private brake = 0;
  private t = 0;
  private readonly history: Seen[] = [];

  /** Gear change the driver wants this step (+1 up, −1 down), for callers that shift via the paddles. */
  shift = 0;

  constructor(
    private readonly line: ReadonlyArray<{ x: number; z: number }>,
    factor = 0.93,
    private readonly human: HumanLimits = HUMAN_LIMITS,
    /** Shift the car's gearbox directly (headless); false = only report `shift`. */
    private readonly shiftsCar = true,
  ) {
    this.pilot = new Autopilot(line, factor, 0.92, DRIVER_TUNING.brakePoints);
  }

  /** The car as the driver has registered it, `delay` seconds ago. */
  private seen(delay: number): Seen {
    const at = this.t - delay;
    let s = this.history[this.history.length - 1]!;
    for (let i = this.history.length - 1; i >= 0 && this.history[i]!.t >= at; i--) s = this.history[i]!;
    return s;
  }

  /** The racing line point `ahead` metres past the car's projection onto the line. */
  private lookahead(car: Car, ahead: number): { x: number; z: number } {
    const line = this.line;
    const n = line.length;
    // Projection onto the segment after (or before) the nearest point.
    let i = this.idx;
    let a = line[i]!, b = line[(i + 1) % n]!;
    let sx = b.x - a.x, sz = b.z - a.z;
    let u = ((car.x - a.x) * sx + (car.z - a.z) * sz) / (sx * sx + sz * sz || 1);
    if (u < 0) {
      i = (i - 1 + n) % n;
      a = line[i]!; b = line[(i + 1) % n]!;
      sx = b.x - a.x; sz = b.z - a.z;
      u = ((car.x - a.x) * sx + (car.z - a.z) * sz) / (sx * sx + sz * sz || 1);
    }
    u = Math.max(0, Math.min(1, u));
    let left = ahead + u * Math.hypot(sx, sz);
    for (let k = 0; k < n; k++) {
      const p = line[(i + k) % n]!, q = line[(i + k + 1) % n]!;
      const len = Math.hypot(q.x - p.x, q.z - p.z);
      if (left <= len) return { x: p.x + ((q.x - p.x) * left) / (len || 1), z: p.z + ((q.z - p.z) * left) / (len || 1) };
      left -= len;
    }
    return line[i]!;
  }

  control(car: Car, dt: number): CarInput {
    this.t += dt;
    this.history.push({ t: this.t, beta: Math.atan2(car.vy, Math.max(car.vx, 1)), rearSpin: car.rearSpin, lock: Math.max(car.frontLock, car.rearLock) });
    const keep = this.t - Math.max(this.human.tracking, this.human.reaction) - 0.05;
    while (this.history.length > 2 && this.history[1]!.t < keep) this.history.shift();
    const tracked = this.seen(this.human.tracking);
    const event = this.seen(this.human.reaction);
    this.pilot.control(car); // updates the target speed
    const line = this.line;
    const n = line.length;
    let best = this.idx, bd = Infinity;
    for (let k = -20; k <= 60; k++) {
      const j = (this.idx + k + n) % n, p = line[j]!;
      const d = (p.x - car.x) ** 2 + (p.z - car.z) ** 2;
      if (d < bd) { bd = d; best = j; }
    }
    this.idx = best;
    // Lookahead point slid smoothly along the line (no jumps from one line point to the next).
    const target = this.lookahead(car, Math.max(8, car.speed * 0.45));
    let err = Math.atan2(target.x - car.x, target.z - car.z) - car.heading;
    err = Math.atan2(Math.sin(err), Math.cos(err));
    const beta = tracked.beta;
    // The front going light through the wheel tells a driver not to wind on more lock
    // than the tyres can use (what the tilt assist does for phones, here as skill).
    const feel = feelLimit(car);
    // Heading error to the lookahead point, softened at speed towards (a multiple of)
    // geometric pure pursuit: a driver doesn't yank 8° of lock at 230 km/h to fix 4 m of line.
    const ld = Math.max(8, car.speed * 0.45);
    const geometric = DRIVER_TUNING.pursuitGeometric * Math.atan((2 * carParams.wheelbase.value * Math.sin(err)) / ld);
    const pursuit = Math.sign(err) * Math.min(Math.abs(DRIVER_TUNING.pursuit * err), Math.abs(geometric));
    // Countersteer: point the front wheels towards the direction of travel when the rear slides.
    const angle = Math.max(-feel, Math.min(feel, pursuit)) + beta * DRIVER_TUNING.countersteer;
    // Arms are a low-pass filter (~3 Hz), and the rim only turns so fast.
    const lock = carParams.steerLockDeg.value * (Math.PI / 180);
    const want = (1 - Math.exp(-dt / 0.05)) * (angle / lock - this.steer);
    const maxRate = (this.human.rimRate / (this.human.rotation / 2)) * dt;
    this.steer += Math.max(-maxRate, Math.min(maxRate, want));
    // Throttle: squeeze on; ease off while the rears spin.
    const friction = tractionThrottle(car);
    if (event.rearSpin > 0.05) this.throttle = Math.max(0, this.throttle - 4 * dt);
    else {
      // Speed control by feel: a steady part-throttle through a corner held at its speed,
      // rather than banging between full throttle and the brake (which lifts mid-corner).
      const speedErr = this.pilot.lastTarget - car.speed;
      const wantThrottle = Math.max(0, Math.min(1, DRIVER_TUNING.holdThrottle + DRIVER_TUNING.throttleGain * speedErr));
      this.throttle += Math.max(-8 * dt, Math.min(1.5 * dt, Math.min(friction, wantThrottle) - this.throttle));
    }
    // Brake: press towards the target, release on a lock-up.
    const overSpeed = car.speed - this.pilot.lastTarget - 1.5;
    const wantBrake = overSpeed > 0 ? Math.min(1, overSpeed * 0.3, brakeThreshold(car) * DRIVER_TUNING.brakeMargin) : 0;
    this.brake =
      event.lock > 0.05
        ? Math.max(0, this.brake - 6 * dt)
        : this.brake + Math.max(-10 * dt, Math.min(6 * dt, wantBrake - this.brake));
    this.shift = car.gearbox.rpm > 11900 && car.gearbox.gear < car.gearbox.gearCount ? 1 : car.gearbox.rpm < 8000 && car.gearbox.gear > 1 ? -1 : 0;
    if (this.shiftsCar && this.shift > 0) car.gearbox.shiftUp();
    else if (this.shiftsCar && this.shift < 0) car.gearbox.shiftDown(car.vx);
    return { steer: Math.max(-1, Math.min(1, this.steer)), throttle: this.throttle, brake: this.brake };
  }
}
